import type {
  GridColDef,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridTreeNodeWithRender,
} from "@mui/x-data-grid-premium";
import { isUndefined } from "lodash-es";
import {
  DataGridDescriptionEditCell,
  DataGridDescriptionViewCell,
} from "../cells/DataGridDescriptionCell.js";
import type { DocDataGrid_ItemFragment } from "../DocDataGrid.generated.js";
import type { ModifyItemInfoFn } from "../useModifyItemInfo.js";
import type { DocDataGridRow, PendingCellEdit } from "../types.js";
import type { DescriptionColumn__ItemFragment } from "./descriptionColumn.generated.js";

interface Options<
  T extends DocDataGrid_ItemFragment & DescriptionColumn__ItemFragment,
> {
  headerName: string;
  canEdit: boolean;
  modifyItemInfo: ModifyItemInfoFn;
  itemCanBeEdited?: (item: T) => boolean;
}

export function createDescriptionColumn<
  T extends DocDataGrid_ItemFragment & DescriptionColumn__ItemFragment,
>(options: Options<T>): GridColDef<DocDataGridRow<T>> {
  return {
    field: "description",
    headerName: options.headerName,
    flex: 1,
    minWidth: 120,
    editable: options.canEdit,
    sortable: false,
    filterable: false,
    valueGetter: (_value, row) => {
      const item = row.item;
      if (!item) return "";
      const pending = item.pendingChangeAttributes?.["description"];
      return !isUndefined(pending) ? pending : item.description;
    },
    valueSetter: (value: string, row) => ({
      ...row,
      item: row.item ? { ...row.item, description: value } : null,
      _pendingEdit: {
        apply: (itemId: string) =>
          options.modifyItemInfo(itemId, { description: value }),
      },
    }),
    renderCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (options.itemCanBeEdited?.(item) === false) return null;
      if (!params.value) return null;
      const canEdit =
        options.canEdit && options.itemCanBeEdited?.(item) !== false;
      return <DataGridDescriptionViewCell params={params} canEdit={canEdit} />;
    },
    renderEditCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (options.itemCanBeEdited?.(item) === false) return null;
      return <DataGridDescriptionEditCell {...params} />;
    },
  };
}
